"use client";

import { logEventAnalytics } from "@/src/features/firebase";

export type TrackingEventPayload = Record<string, unknown>;

/**
 * GA DOM label attribute builder.
 *
 * Produces `data-tracking-eventname` and `data-tracking-event-key-*` attributes
 * consumed by the native app shell for GA event reporting.
 *
 * Use this for:
 * - "GA DOM label / 曝光" events (exposure on container elements)
 * - "GA DOM label + OpenReplay label" events (clicks on button/link elements,
 *   combined with `data-openreplay-label` attribute)
 *
 * Do NOT use this for events without a DOM anchor (SDK callbacks, hook internals,
 * async completion callbacks) — use {@link trackEvent} instead.
 */
export function gaTrackingAttrs(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): Record<string, string | undefined> {
  const attrs: Record<string, string | undefined> = {
    "data-tracking-eventname": eventName,
  };
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== "") {
        attrs[`data-tracking-event-key-${key}`] = String(value);
      }
    }
  }
  return attrs;
}

/**
 * Active GA event reporting (no OpenReplay).
 *
 * Only use when a DOM label is NOT feasible:
 * - Events inside hooks/callbacks without a DOM interaction
 * - SDK callbacks (Plaid, login)
 * - Programmatic navigation events
 * - Async completion callbacks
 *
 * For events tied to DOM elements (exposure or click), prefer
 * {@link gaTrackingAttrs} and `data-openreplay-label` attributes instead.
 */
export function trackEvent(
  event: string,
  payload?: TrackingEventPayload,
): void {
  const normalized = payload
    ? Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined),
      )
    : undefined;
  logEventAnalytics(event, normalized);
}
